import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Equals from '~/bootcamp/Activities/components/Equals'
import Input from '~/bootcamp/Activities/components/Input'
import PEMDASActivityWrapper from '~/bootcamp/Activities/PEMDAS/components/PEMDASActivityWrapper'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import useFocus from '~/bootcamp/Activities/hooks/useFocus'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import { generateTerms } from './helper'
import { getRangesByDifficulty } from './constants'

export default function PlusMinus({
  answerState,
  onAnswerStateChange,
  difficultyLevel,
  onNextQuestionClick,
  onInstanceChange,
  actionButtonsFC,
  autofocus = true,
}: ActivityProps): React.ReactElement {
  const { resetHistory } = getRangesByDifficulty(difficultyLevel)
  const [answer, setAnswer] = useState<string>('')
  const [inputKey, setInputKey] = useState<string>(uuid())
  const [taskHistory, setTaskHistory] = useState<number[]>([])
  const [terms, setTerms] = useState<number[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const { t } = useTranslation('generics')
  useFocus(autofocus, answerState, inputRef)

  useEffect(() => {
    setAnswer('')
    setTerms(generateTerms(difficultyLevel, taskHistory))
  }, [difficultyLevel, taskHistory])

  useEffect(() => {
    onAnswerStateChange(null)
  }, [answer, onAnswerStateChange])

  const expression = terms
    .map((term, index) => (index === 0 ? `${term}` : term >= 0 ? ` + ${term}` : ` - ${Math.abs(term)}`))
    .join('')

  useEmitInstance(onInstanceChange, terms.length > 0 ? `${expression} = \\square` : null)

  const handleOnCheckAnswerClick = (): void => {
    const accumulated = terms.reduce((prev, curr) => prev + curr, 0)
    const isCorrect = parseInt(answer, 10) === accumulated
    onAnswerStateChange(isCorrect)
  }

  const handleOnNextQuestionClick = (): void => {
    const accumulated = terms.reduce((prev, curr) => prev + curr, 0)
    setTaskHistory((prevState) => {
      return [accumulated, ...prevState].slice(0, resetHistory)
    })
    onAnswerStateChange(null)
    onNextQuestionClick()
    setInputKey(uuid())
  }

  const onEnterPress = (): void => {
    if (answer !== '') {
      hocOnEnterPress(answerState, handleOnCheckAnswerClick, handleOnNextQuestionClick)
    }
  }

  return (
    <ActivityWrapper>
      <PEMDASActivityWrapper>
        <span>
          {terms &&
            terms.map((term, index) => {
              return <>{term > 0 ? (index === 0 ? `${term}` : ` + ${term}`) : ` - ${term * -1}`}</>
            })}
        </span>
        <span className="block sm:inline-block">
          <Equals />
          <Input
            aria-label={t('ariaLabelButtons.insertResult')}
            autoFocus={autofocus}
            isError={answerState === false}
            key={inputKey}
            maxDigits={2}
            onChange={(value) => setAnswer(value)}
            onEnter={onEnterPress}
            ref={inputRef}
            value={answer}
          />
        </span>
      </PEMDASActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid: answer !== '',
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleOnCheckAnswerClick,
          clearFormAction: () => setAnswer(''),
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}
